/**
 * Permission catalogue and role mapping.
 *
 * Two rules govern everything here:
 *
 *  1. A permission names a CLASS of object, never a specific one. Holding
 *     `ticket:resolve:team` says you may resolve tickets belonging to your
 *     team; it does not say which ticket. Every handler that acts on a
 *     specific ticket must ALSO pass the object through the visibility scope.
 *     Permission alone is never sufficient authorisation.
 *
 *  2. Actor identity is always derived from the session. `authorId`,
 *     `requesterId`, `actorId` and `visibility` are never read from a request
 *     body, whatever the caller sends.
 */
export const PERMISSIONS = {
    // Ticket lifecycle
    TICKET_CREATE: 'ticket:create',
    TICKET_READ_OWN: 'ticket:read:own',
    TICKET_READ_TEAM: 'ticket:read:team',
    TICKET_READ_ANY: 'ticket:read:any',
    TICKET_UPDATE_ASSIGNED: 'ticket:update:assigned',
    TICKET_UPDATE_ANY: 'ticket:update:any',
    TICKET_TRIAGE: 'ticket:triage',
    TICKET_ASSIGN_TEAM: 'ticket:assign:team',
    TICKET_ASSIGN_ANY: 'ticket:assign:any',
    TICKET_ESCALATE: 'ticket:escalate',
    TICKET_STATUS_TRANSITION: 'ticket:status:transition',
    TICKET_RESOLVE: 'ticket:resolve',
    TICKET_RESOLVE_TEAM: 'ticket:resolve:team',
    TICKET_CONFIRM_RESOLUTION_OWN: 'ticket:confirm-resolution:own',
    TICKET_REOPEN: 'ticket:reopen',
    TICKET_REOPEN_ANY: 'ticket:reopen:any',
    TICKET_CANCEL: 'ticket:cancel',
    /**
     * The requester's own, narrower version of `TICKET_CANCEL`: cancel a
     * ticket they themselves raised, before it is resolved. Held by
     * `END_USER` only — staff act through `TICKET_CANCEL`, which names the
     * broader class ("any ticket cancel handles" is already scoped by the
     * visibility scope, not by requester identity). Object-level enforcement
     * (`assertActorIsRequester`-style) still applies — see
     * `canCancelOwnTicket` below and `REQUIRES_OBJECT_CHECK`.
     */
    TICKET_CANCEL_OWN: 'ticket:cancel:own',
    /** The ONLY way to close without a real user confirmation. Admin only. */
    TICKET_CLOSE_OVERRIDE: 'ticket:close:override',
    TICKET_ADMIN_RESTORE: 'ticket:admin:restore',
    // Content
    COMMENT_CREATE_PUBLIC: 'ticket:comment:create:public',
    COMMENT_CREATE_INTERNAL: 'ticket:comment:create:internal',
    COMMENT_READ_INTERNAL: 'ticket:comment:read:internal',
    ATTACHMENT_UPLOAD: 'ticket:attachment:upload',
    ATTACHMENT_DOWNLOAD_OWN: 'ticket:attachment:download:own',
    ATTACHMENT_DOWNLOAD_TEAM: 'ticket:attachment:download:team',
    PARTICIPANT_MANAGE: 'ticket:participant:manage',
    // Oversight
    AUDIT_READ_ANY: 'ticket:audit:read:any',
    REPORT_READ_TEAM: 'report:read:team',
    REPORT_READ_ANY: 'report:read:any',
    /**
     * Download the person-level ticket/lifecycle export file
     * (`GET /reports/export`). Separate from the READ permissions above because
     * the export is a bulk copy of people's data leaving the system, not an
     * on-screen aggregate. TEAM = the teams the actor leads; ANY = the whole
     * organisation. Each still requires its matching REPORT_READ_* permission.
     */
    REPORT_EXPORT_TEAM: 'report:export:team',
    REPORT_EXPORT_ANY: 'report:export:any',
    // Administration
    ADMIN_USERS_WRITE: 'admin:users:write',
    ADMIN_TEAMS_WRITE: 'admin:teams:write',
    ADMIN_CATALOG_WRITE: 'admin:catalog:write',
    ADMIN_SLA_WRITE: 'admin:sla:write',
    ADMIN_ROUTING_WRITE: 'admin:routing:write',
    ADMIN_SETTINGS_WRITE: 'admin:settings:write',
    /**
     * View the status of, set, replace, remove and test the Azure Blob Storage
     * connection used for ALL new attachment files (Admin → Settings →
     * Attachment storage). The connection string is a long-lived credential, so
     * this is held by the IT Administrator (ADMIN) only — deliberately NOT by
     * TEAM_LEAD, TECHNICIAN, END_USER or AUDITOR, and NOT implied by
     * ADMIN_SETTINGS_WRITE or ADMIN_READ. It is a class-level permission (no
     * per-object check): the resource is a single org-wide setting.
     */
    ADMIN_STORAGE_MANAGE: 'admin:storage:manage',
    /**
     * View, add, edit, test, enable/disable and delete Microsoft 365 tenants
     * (Entra app registrations used for Graph mail intake) and their helpdesk
     * mailboxes (Admin → Settings → Microsoft 365 Tenants). Tenant client
     * secrets are long-lived credentials, so — exactly like
     * ADMIN_STORAGE_MANAGE — this is held by the IT Administrator (ADMIN) only,
     * and is NOT implied by ADMIN_SETTINGS_WRITE, ADMIN_EMAIL_WRITE or
     * ADMIN_READ. Class-level permission: tenants are deployment-wide.
     */
    ADMIN_M365_MANAGE: 'admin:m365:manage',
    ADMIN_EMAIL_WRITE: 'admin:email:write',
    /** Configure Incident Management: workgroups (+ members), floors, SLA hours / calendar and the escalation matrix. */
    ADMIN_INCIDENT_MANAGE: 'admin:incident:manage',
    /**
     * Create, edit, clone, enable/disable and delete CUSTOM roles
     * (Admin → Roles & Permissions). System roles are never editable by
     * anyone — their permissions are defined in this file.
     */
    ADMIN_ROLES_WRITE: 'admin:roles:write',
    /**
     * Grant or remove roles on user accounts. Every grant/removal is further
     * limited server-side: an actor can only grant or remove a role whose
     * permissions they themselves hold (no privilege escalation).
     */
    ADMIN_ROLES_ASSIGN: 'admin:roles:assign',
    /**
     * Read access to the admin configuration area (users, teams, catalogue,
     * SLA, routing, settings) and the admin audit log. Deliberately a single
     * permission rather than one per admin sub-resource: unlike the WRITE
     * permissions above (each gates a specific mutation surface with its own
     * blast radius), there is no meaningful risk difference between reading
     * the team roster and reading the SLA policy list, and a single read gate
     * keeps `packages/db/scripts/seed.mjs`'s automatic permission grant (see
     * `ROLE_PERMISSIONS` below) from needing a matching entry per sub-resource
     * every time the admin module grows one. Held by ADMIN (who also holds
     * every ADMIN_*_WRITE permission and so can always write what they can
     * read) and by AUDITOR (read-only oversight, per its class doc below).
     */
    ADMIN_READ: 'admin:read',
    // Self-service
    NOTIFICATION_PREFS_MANAGE_OWN: 'notification-preferences:manage:own',
    // Incident reports (patient-safety / quality form; separate from tickets).
    /** Start (and submit) an incident report. */
    INCIDENT_CREATE: 'incident:create',
    /** Read incident reports the actor authored. */
    INCIDENT_READ_OWN: 'incident:read:own',
    /** Read every SUBMITTED incident report in the organisation (other people's drafts stay private to their author). */
    INCIDENT_READ_ALL: 'incident:read:all',
    /** Edit and submit the actor's own DRAFT incident reports. */
    INCIDENT_UPDATE_OWN: 'incident:update:own',
    /** Add / remove attachments on the actor's own DRAFT incident reports. */
    INCIDENT_ATTACHMENT_WRITE: 'incident:attachment:write',
    /** View / download attachments of incident reports the actor can read. */
    INCIDENT_ATTACHMENT_READ: 'incident:attachment:read',
    /**
     * Online Incident Management. Read incidents the actor is INVOLVED in: an
     * (active or former) assigned stakeholder, or a member of the incident's
     * workgroup. Object-scoped, so it is part of the baseline: it opens nothing
     * until Quality assigns the person or an admin adds them to a workgroup.
     */
    INCIDENT_READ_INVOLVED: 'incident:read:involved',
    /** Record / submit RCA-CAPA and evidence on incidents the actor is an ACTIVE stakeholder of. Object-scoped (baseline). */
    INCIDENT_RESPOND: 'incident:respond',
    /**
     * The person who RAISED an incident reviews its resolution: accept (closes
     * it) or reject the submitted RCA/CAPA, and reopen it once closed (back to
     * the same stakeholders). Object-scoped to the actor's own reports
     * (baseline), like ticket:confirm-resolution:own.
     */
    INCIDENT_CONFIRM_OWN: 'incident:confirm:own',
    /** Quality: categorise (classification, type, urgency, workgroup) and assign new incidents; add stakeholders. */
    INCIDENT_TRIAGE: 'incident:triage',
    /** Quality: accept, reject or re-assign a submitted RCA/CAPA (reject / re-assign need a remark). */
    INCIDENT_REVIEW: 'incident:review',
    /** Quality: reopen a closed incident and assign it again (needs a remark). */
    INCIDENT_REOPEN: 'incident:reopen',
    /** Download incident reports (Excel/PDF) — limited to what the actor can read. */
    INCIDENT_EXPORT: 'incident:export',
    // IT Invoice Submission (month-by-month vendor invoice tracker per office location).
    // All four are class-level, org-wide permissions (one tracker per organisation);
    // the service still scopes every row to the actor's own organisation.
    /** Open the tracker and see offices, vendors, Circuit ID / Account ID and monthly status. */
    INVOICE_VIEW: 'invoice:view',
    /** Mark a vendor's monthly invoice as Bill Submitted / Invoice Not Submitted. */
    INVOICE_STATUS_MANAGE: 'invoice:status:manage',
    /** Add vendors, rename them, edit their Circuit ID / Account ID, deactivate / reactivate them. */
    INVOICE_VENDORS_MANAGE: 'invoice:vendors:manage',
    /** Configure (recipient, enable/disable, schedule) and test the scheduled invoice reminder email. IT Administrator only by default. */
    INVOICE_REMINDER_MANAGE: 'invoice:reminder:manage',
    // Design Workflow (business request -> content -> design -> approvals -> release).
    // WHO may act at each stage is decided by the workflow configuration
    // (Admin -> Settings -> Design Workflow) and the request's own assignments,
    // never by these permissions alone — every action re-checks the actor
    // against the specific request (see REQUIRES_OBJECT_CHECK).
    /** Use the module at all: see requests you are part of and act where the configuration/assignment allows. Baseline. */
    DESIGN_WORKFLOW_ACCESS: 'design:workflow:access',
    /** Read every submitted design request in the organisation (other people's drafts stay private). */
    DESIGN_REQUEST_READ_ALL: 'design:request:read:all',
    /** Configure regions -> regional coordinators, functional roles, design lead -> designer routing and final approvers. */
    DESIGN_WORKFLOW_CONFIGURE: 'design:workflow:configure',
    // IT Checklist (daily operational checklist completed by IT staff).
    /** Open IT Checklist, submit today's checklist and see one's own history/dashboard. IT staff (TECHNICIAN and above). */
    IT_CHECKLIST_SUBMIT: 'it_checklist:submit',
    /** Change the dashboard history period, manage extra Excel worksheet tabs and rebuild quarterly workbooks. IT Administrator. */
    IT_CHECKLIST_CONFIGURE: 'it_checklist:configure',
    /** IT Checklist Monitor: see every engineer's checklist submissions in the organisation (live feed, summary, details). IT Administrator and Auditor. */
    IT_CHECKLIST_VIEW_ALL: 'it_checklist:view_all',
};
export const ROLES = ['END_USER', 'TECHNICIAN', 'TEAM_LEAD', 'ADMIN', 'AUDITOR'];
const P = PERMISSIONS;
const END_USER = [
    P.TICKET_CREATE,
    P.TICKET_READ_OWN,
    P.COMMENT_CREATE_PUBLIC,
    P.ATTACHMENT_UPLOAD,
    P.ATTACHMENT_DOWNLOAD_OWN,
    P.TICKET_CONFIRM_RESOLUTION_OWN,
    P.TICKET_REOPEN,
    P.TICKET_CANCEL_OWN,
    P.PARTICIPANT_MANAGE,
    P.NOTIFICATION_PREFS_MANAGE_OWN,
    // Anyone who can raise a ticket can file and manage their OWN incident
    // reports. Technician / team lead / admin inherit these through the spread
    // chain below. None of these opens anyone else's report.
    P.INCIDENT_CREATE,
    P.INCIDENT_READ_OWN,
    P.INCIDENT_UPDATE_OWN,
    P.INCIDENT_ATTACHMENT_WRITE,
    P.INCIDENT_ATTACHMENT_READ,
    // Object-scoped: only incidents the person is assigned to / whose workgroup
    // they belong to. Quality assigns hospital staff (HODs, in-charges) who are
    // ordinary accounts, so this must be in the baseline.
    P.INCIDENT_READ_INVOLVED,
    P.INCIDENT_RESPOND,
    P.INCIDENT_CONFIRM_OWN,
    // Everyone may open the Design Workflow module; what they can DO there is
    // decided by the workflow configuration and the request's assignments.
    P.DESIGN_WORKFLOW_ACCESS,
];
const TECHNICIAN = [
    // Staff can confirm a resolution of a ticket THEY raised (own tickets only;
    // the route also needs requester identity). The server refuses when the actor
    // is the resolver of that attempt, so nobody confirms their own work.
    ...END_USER,
    P.TICKET_READ_TEAM,
    P.TICKET_UPDATE_ASSIGNED,
    P.TICKET_TRIAGE,
    P.TICKET_STATUS_TRANSITION,
    P.TICKET_RESOLVE,
    P.TICKET_RESOLVE_TEAM,
    P.COMMENT_CREATE_INTERNAL,
    P.COMMENT_READ_INTERNAL,
    P.ATTACHMENT_DOWNLOAD_TEAM,
    P.TICKET_ESCALATE,
    P.IT_CHECKLIST_SUBMIT,
];
const TEAM_LEAD = [
    ...TECHNICIAN,
    P.TICKET_ASSIGN_TEAM,
    P.TICKET_CANCEL,
    P.TICKET_REOPEN_ANY,
    P.REPORT_READ_TEAM,
    P.REPORT_EXPORT_TEAM,
];
const ADMIN = [
    ...TEAM_LEAD,
    P.TICKET_READ_ANY,
    P.TICKET_UPDATE_ANY,
    P.TICKET_ASSIGN_ANY,
    P.TICKET_CLOSE_OVERRIDE,
    P.TICKET_ADMIN_RESTORE,
    P.AUDIT_READ_ANY,
    P.REPORT_READ_ANY,
    P.REPORT_EXPORT_ANY,
    // Organisation-wide incident visibility is deliberately narrow: ADMIN and
    // (read-only) AUDITOR only. Team leads and technicians see their own reports.
    P.INCIDENT_READ_ALL,
    P.ADMIN_USERS_WRITE,
    P.ADMIN_TEAMS_WRITE,
    P.ADMIN_CATALOG_WRITE,
    P.ADMIN_SLA_WRITE,
    P.ADMIN_ROUTING_WRITE,
    P.ADMIN_SETTINGS_WRITE,
    P.ADMIN_EMAIL_WRITE,
    P.ADMIN_STORAGE_MANAGE,
    P.ADMIN_M365_MANAGE,
    P.ADMIN_ROLES_WRITE,
    P.ADMIN_ROLES_ASSIGN,
    P.ADMIN_READ,
    // IT Invoice Submission: the IT Administrator holds all four. Other access
    // is given through a custom role built on the ADMIN access level (e.g. a
    // view-only "Invoice viewer"), not by widening another system role.
    P.INVOICE_VIEW,
    P.INVOICE_STATUS_MANAGE,
    P.INVOICE_VENDORS_MANAGE,
    P.INVOICE_REMINDER_MANAGE,
    // Online Incident Management (Quality / Incident Management Group powers and
    // configuration). Held by the ADMIN access level because a custom role can
    // only narrow its base level: "Unit Quality", "Corporate Quality" or
    // "Incident Management Group" roles are custom roles built on ADMIN that
    // carry just these (plus INCIDENT_READ_ALL), like the invoice roles above.
    P.INCIDENT_TRIAGE,
    P.INCIDENT_REVIEW,
    P.INCIDENT_REOPEN,
    P.INCIDENT_EXPORT,
    P.ADMIN_INCIDENT_MANAGE,
    P.DESIGN_REQUEST_READ_ALL,
    P.DESIGN_WORKFLOW_CONFIGURE,
    P.IT_CHECKLIST_CONFIGURE,
    P.IT_CHECKLIST_VIEW_ALL,
];
/**
 * Read-only oversight. Holds no write permission of any kind, by design.
 * `ADMIN_READ` is a read, not a write, so it belongs here too — an auditor
 * reviewing SLA policy or routing-rule configuration for a compliance check
 * is exactly the read-only oversight this role exists for.
 */
const AUDITOR = [
    P.TICKET_READ_ANY,
    P.COMMENT_READ_INTERNAL,
    P.AUDIT_READ_ANY,
    P.REPORT_READ_ANY,
    P.ADMIN_READ,
    P.INCIDENT_READ_ALL,
    P.INCIDENT_ATTACHMENT_READ,
    P.DESIGN_REQUEST_READ_ALL,
    P.IT_CHECKLIST_VIEW_ALL,
];
export const ROLE_PERMISSIONS = {
    END_USER: dedupe(END_USER),
    TECHNICIAN: dedupe(TECHNICIAN),
    TEAM_LEAD: dedupe(TEAM_LEAD),
    ADMIN: dedupe(ADMIN),
    AUDITOR: dedupe(AUDITOR),
};
function dedupe(list) {
    return [...new Set(list)];
}
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);
const KNOWN_PERMISSIONS = new Set(ALL_PERMISSIONS);
export function isPermission(code) {
    return KNOWN_PERMISSIONS.has(code);
}
const KNOWN_ROLE_CODES = new Set(ROLES);
/** True for the five built-in (system) role codes. */
export function isSystemRole(code) {
    return KNOWN_ROLE_CODES.has(code);
}
/**
 * How privileged each system role is, used ONLY to (a) collapse several
 * held roles into the single `app.actor_role` value the database triggers
 * and RLS policies read and (b) refuse a grant of a role "above" the
 * granting actor. Never consulted for an ordinary permission decision.
 */
export const ROLE_TIER_RANK = {
    END_USER: 0,
    // Below the write-capable staff tiers on purpose: the `ticket_comments`
    // RLS INSERT/UPDATE policies admit TECHNICIAN/TEAM_LEAD/ADMIN but not
    // AUDITOR, so a technician who also holds an Auditor-based role (say, a
    // report viewer) must still reach the database as a technician or their
    // internal notes would be refused. What an actor can SEE no longer depends
    // on this value (visibility is permission-driven), and the RLS read policy
    // admits AUDITOR and every staff tier alike.
    AUDITOR: 1,
    TECHNICIAN: 2,
    TEAM_LEAD: 3,
    ADMIN: 4,
};
/**
 * CUSTOM ROLES (Admin → Roles & Permissions).
 *
 * A custom role is always BUILT ON one system role — its `baseRole` (shown
 * in the UI as "Access level"). The base role decides what the database
 * itself sees for that user (`app.actor_role`: the RLS policy on
 * `ticket_comments`, the reopen-window trigger) and caps what the custom role
 * may grant: a custom role's permissions must be a subset of its base role's
 * built-in permissions. A custom role therefore can only NARROW an existing,
 * reviewed access level — it can never invent a combination the database
 * layer was not designed for. To give someone more, build the role on a
 * higher base.
 */
export function permissionsAllowedForBase(baseRole) {
    return new Set(ROLE_PERMISSIONS[baseRole]);
}
/**
 * The effective permissions a custom role contributes: its stored codes,
 * filtered to known permissions AND capped by its base role. Applied at
 * read time too (not only on save), so a row edited behind the
 * application's back can still never exceed its base.
 */
export function customRolePermissions(baseRole, stored) {
    const allowed = permissionsAllowedForBase(baseRole);
    const out = new Set();
    for (const code of stored)
        if (isPermission(code) && allowed.has(code))
            out.add(code);
    for (const code of TIER_BOUND_PERMISSIONS)
        if (allowed.has(code))
            out.add(code);
    return out;
}
/**
 * Permissions the DATABASE grants by access level alone, so a custom role
 * always carries them when its base role does — they cannot be switched off
 * per role without the UI promising something the database does not do.
 *
 *   ticket:comment:read:internal — the RLS policy on `ticket_comments`
 *   shows INTERNAL notes to every session whose `app.actor_role` is a staff
 *   role, and that RLS policy is the sole authority on which comment rows
 *   are returned (see `CommentService.list`). A custom role built on a staff
 *   level therefore reads internal notes; one built on END_USER does not.
 */
export const TIER_BOUND_PERMISSIONS = [PERMISSIONS.COMMENT_READ_INTERNAL];
/**
 * The baseline every account gets (the END_USER list: raise tickets, see and
 * reply on your own, confirm your own resolution, ...). Every permission in it
 * is scoped to the holder's OWN tickets, so handing it out is never a
 * privilege escalation — and it must stay grantable by administrators even
 * though staff roles deliberately do not hold `ticket:confirm-resolution:own`
 * themselves (a technician must never confirm a resolution as if they were
 * the requester).
 */
export function isBaselinePermission(code) {
    return ROLE_PERMISSIONS.END_USER.includes(code);
}
/**
 * The no-escalation rule for role grants and role edits (Admin → Roles &
 * Permissions): the permissions in `perms` that the granting actor does NOT
 * hold themselves, ignoring baseline permissions. Non-empty = refuse. The API
 * enforces this (`apps/api/src/modules/admin/roles/role-policy.ts`); the admin
 * UI calls the same function only to grey out what the server would refuse.
 */
export function permissionsBeyond(held, perms) {
    return [...perms].filter((p) => !held.has(p) && !isBaselinePermission(p)).sort();
}
export function permissionsFor(roles) {
    const out = new Set();
    for (const r of roles)
        for (const p of ROLE_PERMISSIONS[r])
            out.add(p);
    return out;
}
export function hasPermission(roles, permission) {
    return permissionsFor(roles).has(permission);
}
/**
 * Permissions that are never sufficient on their own — the handler must also
 * check the specific object against the visibility scope. Keeping this list
 * explicit means a new permission has to make a conscious decision about it.
 */
export const REQUIRES_OBJECT_CHECK = [
    P.TICKET_READ_OWN,
    P.TICKET_READ_TEAM,
    P.TICKET_UPDATE_ASSIGNED,
    P.TICKET_RESOLVE,
    P.TICKET_RESOLVE_TEAM,
    P.TICKET_CONFIRM_RESOLUTION_OWN,
    P.TICKET_ASSIGN_TEAM,
    P.TICKET_REOPEN,
    P.TICKET_CANCEL_OWN,
    P.TICKET_ESCALATE,
    P.COMMENT_CREATE_PUBLIC,
    P.COMMENT_CREATE_INTERNAL,
    P.COMMENT_READ_INTERNAL,
    P.ATTACHMENT_UPLOAD,
    P.ATTACHMENT_DOWNLOAD_OWN,
    P.ATTACHMENT_DOWNLOAD_TEAM,
    P.PARTICIPANT_MANAGE,
    P.REPORT_READ_TEAM,
    P.INCIDENT_READ_OWN,
    P.INCIDENT_READ_ALL,
    P.INCIDENT_UPDATE_OWN,
    P.INCIDENT_ATTACHMENT_WRITE,
    P.INCIDENT_ATTACHMENT_READ,
    P.INCIDENT_READ_INVOLVED,
    P.INCIDENT_RESPOND,
    P.INCIDENT_CONFIRM_OWN,
    P.INCIDENT_TRIAGE,
    P.INCIDENT_REVIEW,
    P.INCIDENT_REOPEN,
    P.INCIDENT_EXPORT,
    P.DESIGN_WORKFLOW_ACCESS,
    P.DESIGN_REQUEST_READ_ALL,
];
export function requiresObjectCheck(permission) {
    return REQUIRES_OBJECT_CHECK.includes(permission);
}
/**
 * The exact predicate `POST /tickets/:id/confirm-resolution` enforces, for
 * BOTH verdicts, split across its two layers:
 *
 *   1. `PermissionsGuard` via `@RequirePermissions(TICKET_CONFIRM_RESOLUTION_OWN)`
 *      on the route (`ticket.controller.ts`).
 *   2. `assertActorIsRequester` inside `ConfirmResolutionService` — the
 *      object-level half, since the permission alone names a class of
 *      ticket, never a specific one (`REQUIRES_OBJECT_CHECK`).
 *
 * `ticket.mapper.ts#availableIntentsFor` calls this SAME function to decide
 * whether to advertise `REJECT_RESOLUTION`, so the UI's idea of "can this
 * actor act on this resolution" and the endpoint's own enforcement cannot
 * drift apart — the alternative (re-deriving the condition in the mapper) is
 * exactly the class of bug this function exists to close off.
 */
export function canActOnOwnResolution(isRequester, permissions) {
    return isRequester && permissions.has(PERMISSIONS.TICKET_CONFIRM_RESOLUTION_OWN);
}
/**
 * The exact predicate `POST /tickets/:id/cancel` enforces for a REQUESTER
 * acting on their own ticket — the same "class permission (route guard) +
 * object-level requester check (service)" split as `canActOnOwnResolution`
 * above, and for the identical reason: `ticket.mapper.ts#availableIntentsFor`
 * calls this SAME function to decide whether to advertise `CANCEL` to a
 * requester, so the UI and the endpoint's own enforcement cannot drift apart.
 *
 * Staff cancel through the separate, broader `TICKET_CANCEL` permission,
 * which does not require this predicate — see `TicketWorkflowService.cancel`.
 */
export function canCancelOwnTicket(isRequester, permissions) {
    return isRequester && permissions.has(PERMISSIONS.TICKET_CANCEL_OWN);
}
//# sourceMappingURL=permissions.js.map